
import React from 'react';
// Added wildcard import for dynamic icon loading and fallback reference
import * as LucideIcons from 'lucide-react';
import { Palmtree, ArrowUp, Mail, Shield, Sparkles, Loader2, Lock } from 'lucide-react';
import { SiteContent, SocialLink } from '../types';
import { optimizeContentAI } from '../services/geminiService';

interface FooterProps {
  onToggleAdmin: () => void;
  isAdmin: boolean;
  data: SiteContent['footer'];
  onUpdate: (data: SiteContent['footer']) => void;
  onNavigate: (view: any) => void;
  siteName: string;
  logoUrl?: string;
}

const Footer: React.FC<FooterProps> = ({ onToggleAdmin, isAdmin, data, onUpdate, onNavigate, siteName, logoUrl }) => {
  const [isOptimizing, setIsOptimizing] = React.useState<string | null>(null);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOptimize = async (field: keyof SiteContent['footer']) => {
    setIsOptimizing(field as string);
    const textToOptimize = data[field];
    if (typeof textToOptimize === 'string') {
      const optimized = await optimizeContentAI(textToOptimize, `Footer ${field}`);
      onUpdate({ ...data, [field]: optimized });
    }
    setIsOptimizing(null);
  };

  const EXPLORE_LINKS = [
    { label: 'Island Heritage', id: 'discover' },
    { label: 'Wild Adventures', id: 'activities' },
    { label: 'Travel Guide', id: 'guide' },
    { label: 'Photo Gallery', id: 'gallery' },
    { label: 'Travel Tips', id: 'tips' }
  ];

  const updateSocialLink = (platform: string, newUrl: string) => {
    onUpdate({
      ...data,
      socialLinks: data.socialLinks.map(link => link.platform === platform ? { ...link, url: newUrl } : link)
    });
  };

  return (
    <footer className="relative bg-slate-950 pt-24 pb-12 overflow-hidden" role="contentinfo">
      {/* Subtle background glow */}
      <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-sky-500/5 to-transparent pointer-events-none"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          
          <div className="lg:col-span-4">
            <div className="flex items-center gap-4 mb-10 group cursor-pointer" onClick={() => onNavigate('home')}>
              {logoUrl ? (
                <div className="bg-white p-4 rounded-[2rem] shadow-2xl">
                  <img src={logoUrl} alt={`${siteName} logo`} className="h-16 w-auto object-contain" />
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="p-2.5 bg-white text-slate-950 rounded-2xl group-hover:bg-sky-500 group-hover:text-white transition-all duration-500">
                    <Palmtree className="w-6 h-6" />
                  </div>
                  <span className="text-lg font-bold tracking-[0.3em] uppercase text-white">{siteName}</span>
                </div>
              )}
            </div>
            
            {isAdmin ? (
              <div className="w-full relative group mb-8">
                <textarea 
                  value={data.brandDesc}
                  onChange={e => onUpdate({...data, brandDesc: e.target.value})}
                  className="w-full bg-white/5 text-slate-300 text-sm leading-relaxed mb-4 p-6 rounded-3xl border border-white/10 focus:outline-none focus:border-sky-500/50 h-32"
                />
                <button onClick={() => handleOptimize('brandDesc')} className="absolute top-4 right-4 text-sky-400">
                   {isOptimizing === 'brandDesc' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                </button>
              </div>
            ) : (
              <p className="text-slate-400 text-lg font-light leading-relaxed mb-8 italic font-serif">"{data.brandDesc}"</p>
            )}
            
            <div className="flex flex-wrap gap-3">
              {data.socialLinks.map((link) => {
                const Icon = (LucideIcons as any)[link.platform] || LucideIcons.Link;
                return (
                  <div key={link.platform} className="group relative">
                    {isAdmin ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-[7px] font-bold text-white uppercase">{link.platform}</span>
                        <input 
                          value={link.url}
                          onChange={e => updateSocialLink(link.platform, e.target.value)}
                          className="bg-white/5 border border-white/10 rounded px-2 py-1 text-[8px] text-white w-28 focus:outline-none"
                        />
                      </div>
                    ) : (
                      <a href={link.url} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center transition-all hover:bg-white hover:text-slate-950 group" aria-label={`Follow Balut Island on ${link.platform}`}>
                        <Icon className="w-4 h-4 text-slate-500 group-hover:text-slate-950 transition-colors" />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2 lg:col-start-6">
            {isAdmin ? (
              <input 
                value={data.column1Header}
                onChange={e => onUpdate({...data, column1Header: e.target.value})}
                className="bg-transparent text-[10px] font-bold uppercase tracking-luxury text-white mb-6 focus:outline-none border-b border-white/10"
              />
            ) : (
              <h4 className="text-[10px] font-bold uppercase tracking-luxury text-white mb-6">{data.column1Header}</h4>
            )}
            <ul className="space-y-4">
              {EXPLORE_LINKS.map((link) => (
                <li key={link.label}>
                  <button onClick={() => onNavigate(link.id)} className="text-slate-400 text-xs font-medium hover:text-sky-400 transition-colors uppercase tracking-widest text-left">
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            {isAdmin ? (
              <input 
                value={data.column2Header}
                onChange={e => onUpdate({...data, column2Header: e.target.value})}
                className="bg-transparent text-[10px] font-bold uppercase tracking-luxury text-white mb-6 focus:outline-none border-b border-white/10"
              />
            ) : (
              <h4 className="text-[10px] font-bold uppercase tracking-luxury text-white mb-6">{data.column2Header}</h4>
            )}
            <ul className="space-y-4">
              <li><button onClick={() => onNavigate('contact')} className="text-slate-400 text-xs font-medium hover:text-sky-400 transition-colors uppercase tracking-widest text-left">Inquiries</button></li>
              <li><button onClick={() => onNavigate('home')} className="text-slate-400 text-xs font-medium hover:text-sky-400 transition-colors uppercase tracking-widest text-left">Privacy Policy</button></li>
              <li>
                <button 
                  onClick={onToggleAdmin} 
                  className="flex items-center gap-2 text-slate-500 text-[10px] font-bold uppercase tracking-luxury hover:text-sky-400 transition-all text-left group"
                >
                  <Lock className={`w-3 h-3 ${isAdmin ? 'text-sky-500' : 'text-slate-600 group-hover:text-sky-400'}`} />
                  {isAdmin ? 'Staff Mode Active' : 'Staff Access'}
                </button>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            {isAdmin ? (
              <input 
                value={data.newsletterTitle}
                onChange={e => onUpdate({...data, newsletterTitle: e.target.value})}
                className="bg-transparent text-[10px] font-bold uppercase tracking-luxury text-white mb-6 focus:outline-none border-b border-white/10"
              />
            ) : (
              <h4 className="text-[10px] font-bold uppercase tracking-luxury text-white mb-6">{data.newsletterTitle}</h4>
            )}
            <div className="space-y-6">
              {isAdmin ? (
                <textarea 
                  value={data.newsletterDesc}
                  onChange={e => onUpdate({...data, newsletterDesc: e.target.value})}
                  className="w-full bg-white/5 text-slate-400 text-xs leading-relaxed p-4 rounded-2xl border border-white/10 focus:outline-none h-20"
                />
              ) : (
                <p className="text-slate-400 text-xs leading-relaxed">{data.newsletterDesc}</p>
              )}
              <div className="relative group">
                <input 
                  type="email" 
                  placeholder="Your Email Address" 
                  className="w-full bg-transparent border-b border-white/10 py-3 text-xs font-light text-white focus:outline-none focus:border-sky-500 transition-all placeholder:text-slate-600" 
                  aria-label="Newsletter email subscription"
                />
                <button className="absolute right-0 top-1/2 -translate-y-1/2 text-sky-500 hover:text-white transition-colors" aria-label="Subscribe to newsletter">
                  <Mail className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8">
          {isAdmin ? (
            <input 
              value={data.copyright}
              onChange={e => onUpdate({...data, copyright: e.target.value})}
              className="bg-transparent text-slate-500 text-[9px] font-bold uppercase tracking-luxury focus:outline-none border-b border-white/10 w-full md:w-96"
            />
          ) : (
            <p className="text-slate-500 text-[9px] font-bold uppercase tracking-luxury">© {new Date().getFullYear()} {data.copyright}</p>
          )}
          
          <button onClick={scrollToTop} className="group flex items-center gap-6 text-slate-500 hover:text-white transition-all text-[9px] font-bold uppercase tracking-luxury" aria-label="Scroll back to the top of the page">
            Return to Horizon
            <div className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center group-hover:bg-white group-hover:text-slate-950 group-hover:border-white transition-all duration-500">
              <ArrowUp className="w-4 h-4" />
            </div>
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
